import { useEffect } from 'react';
import { Alert, Button, Card, Caption, Form, FormActions, Heading, Stack } from '@srichaitra/ui';
import { FormField, useForm } from '@srichaitra/common';
import { labels } from '@srichaitra/constants';
import { loginSchema } from '@srichaitra/validation';
import {
  clearAuthError,
  selectAuthError,
  selectAuthStatus,
  selectSessionExpired,
  signIn,
  useAppDispatch,
  useAppSelector
} from '@srichaitra/store';

/**
 * Sign-in.
 *
 * The page owns no state of its own. The form is the shared useForm hook driven
 * by the shared yup schema, the outcome lives in the auth slice, and every
 * string comes from the label catalogue — so this file is only the arrangement
 * of those pieces, which is the whole intent of the structure.
 *
 * The failure banner is deliberately the server's answer rather than anything
 * this page decides. INVALID_CREDENTIALS, ACCOUNT_LOCKED and RATE_LIMITED all
 * mean different things to the person reading, and the message catalogue
 * already knows how to say each of them.
 */
export function LoginPage() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const expired = useAppSelector(selectSessionExpired);

  // A stale banner from a previous attempt should not greet the next visit.
  useEffect(() => () => dispatch(clearAuthError()), [dispatch]);

  const form = useForm({
    schema: loginSchema,
    onSubmit: (values) => dispatch(signIn(values))
  });

  const busy = status === 'signingIn' || form.submitting;

  return (
    <Card padding={6}>
      <Form onSubmit={form.handleSubmit}>
        <Stack gap={1}>
          <Heading level={2} size="h2">
            {labels.login.heading}
          </Heading>
          <Caption>{labels.login.subheading}</Caption>
        </Stack>

        {expired ? <Alert tone="warning">{labels.login.sessionExpired}</Alert> : null}
        {error ? <Alert tone="error">{error.message}</Alert> : null}

        <Stack gap={4}>
          <FormField
            label={labels.login.userId}
            field={form.fieldProps('userId')}
            required
            placeholder={labels.login.userIdPlaceholder}
            autoComplete="username"
            autoFocus
          />

          <FormField
            label={labels.login.password}
            control="password"
            field={form.fieldProps('password')}
            required
            placeholder={labels.login.passwordPlaceholder}
            autoComplete="current-password"
          />
        </Stack>

        <FormActions>
          <Button type="submit" size="lg" fullWidth loading={busy}>
            {busy ? labels.login.submitting : labels.login.submit}
          </Button>
        </FormActions>
      </Form>
    </Card>
  );
}
